<template>
  <b-modal
    v-model="modalVisible"
    :title="isEditMode ? 'Edit Email Template' : 'Add Email Template'"
    size="xl"
    no-close-on-backdrop
    no-close-on-esc
    :ok-title="isEditMode ? 'Update' : 'Save'"
    :ok-disabled="submitting"
    :cancel-disabled="submitting"
    @ok.prevent="handleSubmit"
    @hidden="$emit('modal-closed')"
  >
    <b-alert
      variant="danger"
      :show="submitError ? true : false"
    >
      <div class="alert-body">
        <p>{{ submitError }}</p>
      </div>
    </b-alert>

    <b-form @submit.prevent="handleSubmit">
      <b-row>
        <b-col md="6">
          <b-form-group
            label="Name"
            label-for="template-name"
          >
            <b-form-input
              id="template-name"
              v-model="form.name"
              placeholder="Enter template name"
              :state="nameState"
              required
            />
            <b-form-invalid-feedback v-if="nameState === false">
              Name is required
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group
            label="Description"
            label-for="template-description"
          >
            <b-form-input
              id="template-description"
              v-model="form.description"
              placeholder="Short description for this template"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-form-group
            label="Load from Default Template"
            label-for="default-template-select"
          >
            <b-form-select
              id="default-template-select"
              v-model="selectedDefaultTemplate"
              :options="defaultTemplateOptions"
              @change="applyDefaultTemplate"
            />
            <small class="text-muted">
              Selecting a default template will prefill name, description and content.
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-form-group
            label="Content"
            label-for="template-content"
          >
            <!-- Toolbar -->
            <div class="editor-toolbar">
              <div class="toolbar-group">
                <button
                  class="toolbar-btn"
                  :class="{ active: activeFormats.bold }"
                  title="Bold"
                  type="button"
                  @mousedown.prevent
                  @click="execCmd('bold')"
                >
                  <strong>B</strong>
                </button>
                <button
                  class="toolbar-btn"
                  :class="{ active: activeFormats.italic }"
                  title="Italic"
                  type="button"
                  @mousedown.prevent
                  @click="execCmd('italic')"
                >
                  <em>I</em>
                </button>
                <button
                  class="toolbar-btn"
                  :class="{ active: activeFormats.underline }"
                  title="Underline"
                  type="button"
                  @mousedown.prevent
                  @click="execCmd('underline')"
                >
                  <u>U</u>
                </button>
                <button
                  class="toolbar-btn"
                  :class="{ active: activeFormats.strikeThrough }"
                  title="Strikethrough"
                  type="button"
                  @mousedown.prevent
                  @click="execCmd('strikeThrough')"
                >
                  <s>S</s>
                </button>
              </div>

              <span class="toolbar-divider" />

              <div class="toolbar-group">
                <select
                  v-model="selectedFontSize"
                  class="toolbar-select"
                  title="Font Size"
                  @mousedown="saveSelection"
                  @change="onFontSizeChange"
                >
                  <option value="">
                    Size
                  </option>
                  <option value="1">
                    Small
                  </option>
                  <option value="3">
                    Normal
                  </option>
                  <option value="4">
                    Large
                  </option>
                  <option value="5">
                    X-Large
                  </option>
                  <option value="7">
                    Huge
                  </option>
                </select>
              </div>

              <span class="toolbar-divider" />

              <div class="toolbar-group">
                <label
                  class="toolbar-btn color-btn"
                  title="Font Color"
                  @mousedown.prevent="saveSelection"
                >
                  <span class="color-letter">A</span>
                  <span
                    class="color-bar"
                    :style="{ background: fontColor }"
                  />
                  <input
                    v-model="fontColor"
                    type="color"
                    class="sr-only"
                    @input="applyFontColor"
                  >
                </label>
                <label
                  class="toolbar-btn color-btn"
                  title="Highlight"
                  @mousedown.prevent="saveSelection"
                >
                  <feather-icon
                    icon="Edit3Icon"
                    size="13"
                  />
                  <span
                    class="color-bar"
                    :style="{ background: highlightColor }"
                  />
                  <input
                    v-model="highlightColor"
                    type="color"
                    class="sr-only"
                    @input="applyHighlightColor"
                  >
                </label>
              </div>

              <span class="toolbar-divider" />

              <div class="toolbar-group">
                <button
                  class="toolbar-btn"
                  title="Align Left"
                  type="button"
                  @mousedown.prevent
                  @click="execCmd('justifyLeft')"
                >
                  <feather-icon
                    icon="AlignLeftIcon"
                    size="14"
                  />
                </button>
                <button
                  class="toolbar-btn"
                  title="Align Center"
                  type="button"
                  @mousedown.prevent
                  @click="execCmd('justifyCenter')"
                >
                  <feather-icon
                    icon="AlignCenterIcon"
                    size="14"
                  />
                </button>
                <button
                  class="toolbar-btn"
                  title="Align Right"
                  type="button"
                  @mousedown.prevent
                  @click="execCmd('justifyRight')"
                >
                  <feather-icon
                    icon="AlignRightIcon"
                    size="14"
                  />
                </button>
              </div>

              <span class="toolbar-divider" />

              <div class="toolbar-group">
                <button
                  class="toolbar-btn"
                  title="Bullet List"
                  type="button"
                  @mousedown.prevent
                  @click="execCmd('insertUnorderedList')"
                >
                  <feather-icon
                    icon="ListIcon"
                    size="14"
                  />
                </button>
                <button
                  class="toolbar-btn"
                  title="Numbered List"
                  type="button"
                  @mousedown.prevent
                  @click="execCmd('insertOrderedList')"
                >
                  <feather-icon
                    icon="HashIcon"
                    size="14"
                  />
                </button>
              </div>

              <span class="toolbar-divider" />

              <div class="toolbar-group">
                <button
                  class="toolbar-btn"
                  title="Insert Link"
                  type="button"
                  @mousedown.prevent
                  @click="showLinkModal = true"
                >
                  <feather-icon
                    icon="LinkIcon"
                    size="14"
                  />
                </button>
                <button
                  class="toolbar-btn"
                  title="Insert Table"
                  type="button"
                  @mousedown.prevent
                  @click="showTableModal = true"
                >
                  <feather-icon
                    icon="GridIcon"
                    size="14"
                  />
                </button>
                <button
                  class="toolbar-btn"
                  title="Horizontal Rule"
                  type="button"
                  @mousedown.prevent
                  @click="insertHorizontalRule"
                >
                  <feather-icon
                    icon="MinusIcon"
                    size="14"
                  />
                </button>
              </div>

              <span class="toolbar-divider" />

              <div class="toolbar-group">
                <button
                  class="toolbar-btn toolbar-btn-text"
                  title="Insert Name Placeholder"
                  type="button"
                  @click="insertNamePlaceholder"
                >
                  <feather-icon
                    icon="UserIcon"
                    size="13"
                  />
                  <span>Name</span>
                </button>
              </div>

              <div class="toolbar-spacer" />

              <div class="toolbar-group">
                <button
                  class="toolbar-btn toolbar-btn-text"
                  :class="{ active: signatureInserted }"
                  title="Signature"
                  type="button"
                  @click="toggleSignature"
                >
                  <feather-icon
                    icon="CheckSquareIcon"
                    size="13"
                  />
                  <span>Signature</span>
                </button>
                <button
                  class="toolbar-btn toolbar-btn-text"
                  :class="{ active: showHtmlSource }"
                  title="HTML Source"
                  type="button"
                  @click="toggleHtmlView"
                >
                  <feather-icon
                    icon="CodeIcon"
                    size="13"
                  />
                  <span>HTML</span>
                </button>
              </div>
            </div>

            <!-- Editor Body -->
            <div class="editor-body">
              <div
                v-show="!showHtmlSource"
                ref="editorContent"
                class="editor-content"
                contenteditable="true"
                @input="onContentInput"
                @mouseup="updateActiveFormats"
                @keyup="updateActiveFormats"
                @paste="onPaste"
              />
              <textarea
                v-show="showHtmlSource"
                ref="htmlSource"
                v-model="form.content"
                class="html-source"
                spellcheck="false"
                @input="onHtmlSourceInput"
              />
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>

    <!-- Insert Link Modal -->
    <b-modal
      v-model="showLinkModal"
      title="Insert Link"
      size="sm"
      ok-title="Insert"
      @ok="confirmInsertLink"
      @hidden="linkUrl = 'https://'"
    >
      <b-form-group
        label="URL"
        label-size="sm"
      >
        <b-form-input
          v-model="linkUrl"
          size="sm"
          placeholder="https://example.com"
        />
      </b-form-group>
    </b-modal>

    <!-- Insert Table Modal -->
    <b-modal
      v-model="showTableModal"
      title="Insert Table"
      size="sm"
      ok-title="Insert"
      @ok="confirmInsertTable"
      @hidden="tableRows = 3; tableCols = 3"
    >
      <b-row>
        <b-col>
          <b-form-group
            label="Rows"
            label-size="sm"
          >
            <b-form-input
              v-model.number="tableRows"
              type="number"
              size="sm"
              min="1"
              max="20"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="Columns"
            label-size="sm"
          >
            <b-form-input
              v-model.number="tableCols"
              type="number"
              size="sm"
              min="1"
              max="10"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
  </b-modal>
</template>

<script>
import {
  BModal, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BFormSelect,
  BRow, BCol, BAlert,
} from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'AddEmailTemplate',

  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormSelect,
    BRow,
    BCol,
    BAlert,
  },

  props: {
    templateToEdit: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      modalVisible: true,
      submitting: false,
      submitError: null,

      form: {
        name: '',
        description: '',
        content: '',
      },
      nameTouched: false,

      selectedDefaultTemplate: null,

      // Editor state
      showHtmlSource: false,
      signatureInserted: false,
      selectedFontSize: '',
      fontColor: '#000000',
      highlightColor: '#ffff00',
      activeFormats: {
        bold: false,
        italic: false,
        underline: false,
        strikeThrough: false,
      },

      // Modal data
      showLinkModal: false,
      linkUrl: 'https://',
      showTableModal: false,
      tableRows: 3,
      tableCols: 3,

      savedRange: null,
    }
  },

  computed: {
    isEditMode() {
      return !!(this.templateToEdit && this.templateToEdit.id)
    },

    nameState() {
      if (!this.nameTouched) return null
      return this.form.name.trim().length > 0
    },

    defaultTemplates() {
      return [
        {
          name: 'Ledger Request FY',
          description: 'Request ledger for a financial year',
          html: `<p>Dear Sir/Ma'am,</p>
<p>Kindly provide the Ledger for <strong>FY 20__ - 20__</strong> in EXCEL / CSV format.</p>
<p>Warm Regards,</p>`,
        },
        {
          name: 'Ledger Request Custom Date',
          description: 'Request ledger for a custom date range',
          html: `<p>Dear Sir/Ma'am,</p>
<p>Kindly provide the Ledger from <strong>1/04/____</strong> to <strong>__/__/____</strong> in EXCEL / CSV format.</p>
<p>Warm Regards,</p>`,
        },
        {
          name: 'Credit Note Request',
          description: 'Request pending credit notes with details table',
          html: `<p>Dear Sir/Ma'am,</p>
<p>Kindly provide the Credit Notes mentioned in the below table.<br>We would request you to Email the Credit Notes as soon as they are created to avoid delays.</p>
<table border="1" cellpadding="8" cellspacing="0" style="border-collapse:collapse; border-color:#ddd; width:100%; margin:12px 0;">
<tr style="background:#f0f0f0;"><th style="text-align:left; padding:8px; text-transform:uppercase; letter-spacing:0.5px;">CN NUMBER</th><th style="text-align:left; padding:8px; text-transform:uppercase; letter-spacing:0.5px;">CN DATE</th><th style="text-align:left; padding:8px; text-transform:uppercase; letter-spacing:0.5px;">CN AMOUNT</th></tr>
<tr><td style="padding:8px;">&nbsp;</td><td style="padding:8px;">&nbsp;</td><td style="padding:8px;">&nbsp;</td></tr>
<tr><td style="padding:8px;">&nbsp;</td><td style="padding:8px;">&nbsp;</td><td style="padding:8px;">&nbsp;</td></tr>
</table>
<p>Warm Regards,</p>`,
        },
        {
          name: 'Payment Details',
          description: 'Request details for a payment made',
          html: `<p>Dear Sir/Ma'am,</p>
<p>Kindly provide the payment details for the below payment.</p>
<table border="1" cellpadding="8" cellspacing="0" style="border-collapse:collapse; border-color:#ddd; width:100%; margin:12px 0;">
<tr style="background:#f0f0f0;"><th style="text-align:left; padding:8px; text-transform:uppercase; letter-spacing:0.5px;">PAYMENT AMT</th><th style="text-align:left; padding:8px; text-transform:uppercase; letter-spacing:0.5px;">PAYMENT DATE</th><th style="text-align:left; padding:8px; text-transform:uppercase; letter-spacing:0.5px;">CHEQUE NO</th></tr>
<tr><td style="padding:8px;">&nbsp;</td><td style="padding:8px;">&nbsp;</td><td style="padding:8px;">&nbsp;</td></tr>
<tr><td style="padding:8px;">&nbsp;</td><td style="padding:8px;">&nbsp;</td><td style="padding:8px;">&nbsp;</td></tr>
</table>
<p>Warm Regards,</p>`,
        },
        {
          name: 'Blank',
          description: 'Start from scratch',
          html: '<p><br></p>',
        },
      ]
    },

    defaultTemplateOptions() {
      const options = [{ value: null, text: '-- Select a default template --' }]
      this.defaultTemplates.forEach((tpl, idx) => {
        options.push({ value: idx, text: tpl.name })
      })
      return options
    },

    signatureHtml() {
      /* eslint-disable max-len */
      return `<div style="margin-top:24px; padding-top:16px; border-top:2px solid #0178BA;">
  <table cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif; font-size:13px; color:#333;">
    <tr>
      <td style="padding-right:14px; vertical-align:middle;">
        <img src="https://erp.vatikadistributors.com/static/email_signature/logo.png" width="150" height="52" alt="Vatika Distributors" style="display:block; border:0;" />
      </td>
      <td style="vertical-align:top; padding:0 14px; border-left:2px solid #0178BA; border-right:2px solid #0178BA;">
        <div style="font-size:16px; font-weight:900; color:#E5172C; margin-bottom:0; letter-spacing:0.5px; text-transform:uppercase;">Vatika Distributors Pvt. Ltd.</div>
        <div style="font-size:9px; font-weight:700; color:#0178BA; letter-spacing:2px; text-transform:uppercase; margin-bottom:8px;">Pharmaceutical Wholesalers</div>
        <table cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif; font-size:11.5px; color:#333; border-collapse:collapse; width:100%;">
          <tr>
            <td style="padding:2px 0; vertical-align:middle; width:16px;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/phone.png" width="12" height="12" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:2px 0; font-weight:700; color:#333; width:65px;">Sales</td>
            <td style="padding:2px 0; white-space:nowrap;"><a href="tel:06573500333" style="color:#0178BA; text-decoration:none; font-weight:500;">0657-3500333</a></td>
            <td style="padding:2px 0 2px 10px; white-space:nowrap;">
              <a href="mailto:sales@vatikadistributors.com" style="color:#0178BA; text-decoration:none;">
                <img src="https://erp.vatikadistributors.com/static/email_signature/email_icon.png" width="11" height="11" alt="" style="display:inline; vertical-align:middle; margin-right:3px; border:0;" /><span style="font-size:10.5px;">sales@vatikadistributors.com</span>
              </a>
            </td>
          </tr>
          <tr>
            <td style="padding:2px 0; vertical-align:middle;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/phone.png" width="12" height="12" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:2px 0; font-weight:700; color:#333;">Accounts</td>
            <td style="padding:2px 0;"><a href="tel:06573500332" style="color:#0178BA; text-decoration:none; font-weight:500;">0657-3500332</a></td>
            <td style="padding:2px 0 2px 10px;">
              <a href="mailto:accounts@vatikadistributors.com" style="color:#0178BA; text-decoration:none;">
                <img src="https://erp.vatikadistributors.com/static/email_signature/email_icon.png" width="11" height="11" alt="" style="display:inline; vertical-align:middle; margin-right:3px; border:0;" /><span style="font-size:10.5px;">accounts@vatikadistributors.com</span>
              </a>
            </td>
          </tr>
          <tr>
            <td style="padding:2px 0; vertical-align:middle;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/phone.png" width="12" height="12" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:2px 0; font-weight:700; color:#333;">Expiry</td>
            <td style="padding:2px 0;"><a href="tel:06573500331" style="color:#0178BA; text-decoration:none; font-weight:500;">0657-3500331</a></td>
            <td style="padding:2px 0 2px 10px;">
              <a href="mailto:expiry@vatikadistributors.com" style="color:#0178BA; text-decoration:none;">
                <img src="https://erp.vatikadistributors.com/static/email_signature/email_icon.png" width="11" height="11" alt="" style="display:inline; vertical-align:middle; margin-right:3px; border:0;" /><span style="font-size:10.5px;">expiry@vatikadistributors.com</span>
              </a>
            </td>
          </tr>
          <tr>
            <td style="padding:2px 0; vertical-align:middle;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/phone.png" width="12" height="12" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:2px 0; font-weight:700; color:#333;">Purchase</td>
            <td style="padding:2px 0;"><a href="tel:06573500330" style="color:#0178BA; text-decoration:none; font-weight:500;">0657-3500330</a></td>
            <td style="padding:2px 0 2px 10px;">
              <a href="mailto:purchase@vatikadistributors.com" style="color:#0178BA; text-decoration:none;">
                <img src="https://erp.vatikadistributors.com/static/email_signature/email_icon.png" width="11" height="11" alt="" style="display:inline; vertical-align:middle; margin-right:3px; border:0;" /><span style="font-size:10.5px;">purchase@vatikadistributors.com</span>
              </a>
            </td>
          </tr>
        </table>
      </td>
      <td style="vertical-align:top; padding-left:14px;">
        <table cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif; font-size:11.5px; color:#333; border-collapse:collapse;">
          <tr>
            <td style="padding:0 0 12px 0; vertical-align:middle; width:20px;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/whatsapp.png" width="16" height="16" alt="WhatsApp" style="display:block; border:0;" />
            </td>
            <td style="padding:0 0 12px 4px;">
              <a href="https://wa.me/919204251917" target="_blank" style="color:#25D366; text-decoration:none; font-weight:700; font-size:13px;">9204251917</a>
            </td>
          </tr>
          <tr>
            <td style="padding:0; vertical-align:top;">
              <img src="https://erp.vatikadistributors.com/static/email_signature/pin.png" width="14" height="14" alt="" style="display:block; border:0;" />
            </td>
            <td style="padding:0 0 0 4px; color:#555; line-height:1.5; font-size:11px;">
              76 A, Pennar Road,<br>SNP Area, Sakchi,<br>Jamshedpur-831001
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</div>`
      /* eslint-enable max-len */
    },
  },

  mounted() {
    // Prefill reactive form fields immediately
    if (this.isEditMode) {
      this.form.name = this.templateToEdit.name || ''
      this.form.description = this.templateToEdit.description || ''
      this.form.content = this.templateToEdit.content || ''
    }
    // Push content into contenteditable div once it actually exists in DOM.
    // b-modal renders lazily, so a single $nextTick isn't always enough.
    this.prefillEditorContent()
  },

  methods: {
    // --- Prefill (handles b-modal lazy rendering) ---
    prefillEditorContent(attempts = 0) {
      if (!this.form.content) return
      if (this.$refs.editorContent) {
        this.$refs.editorContent.innerHTML = this.form.content
        // Detect signature if already in edited content
        if (this.form.content.includes('data-signature="true"')) {
          this.signatureInserted = true
        }
        return
      }
      if (attempts > 20) return // give up after ~600ms
      setTimeout(() => this.prefillEditorContent(attempts + 1), 30)
    },

    // --- Default Template Apply ---
    applyDefaultTemplate() {
      if (this.selectedDefaultTemplate === null) return
      const tpl = this.defaultTemplates[this.selectedDefaultTemplate]
      if (!tpl) return

      this.form.name = tpl.name
      this.form.description = tpl.description
      this.form.content = tpl.html
      this.signatureInserted = false
      this.$nextTick(() => {
        if (this.$refs.editorContent) {
          this.$refs.editorContent.innerHTML = tpl.html
        }
      })
    },

    // --- Core Editor ---
    execCmd(command, val = null) {
      this.$refs.editorContent.focus()
      document.execCommand(command, false, val) // eslint-disable-line no-restricted-properties
      this.syncContent()
      this.updateActiveFormats()
    },

    syncContent() {
      this.form.content = this.$refs.editorContent.innerHTML
    },

    onContentInput() {
      this.syncContent()
    },

    onHtmlSourceInput() {
      this.$refs.editorContent.innerHTML = this.form.content
    },

    onPaste(e) {
      e.preventDefault()
      const html = e.clipboardData.getData('text/html')
      const text = e.clipboardData.getData('text/plain')
      this.execCmd('insertHTML', html || text)
    },

    updateActiveFormats() {
      this.activeFormats.bold = document.queryCommandState('bold')
      this.activeFormats.italic = document.queryCommandState('italic')
      this.activeFormats.underline = document.queryCommandState('underline')
      this.activeFormats.strikeThrough = document.queryCommandState('strikeThrough')
    },

    onFontSizeChange() {
      if (this.selectedFontSize) {
        this.restoreSelection()
        this.execCmd('fontSize', this.selectedFontSize)
        this.selectedFontSize = ''
      }
    },

    saveSelection() {
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        this.savedRange = sel.getRangeAt(0).cloneRange()
      }
    },

    restoreSelection() {
      if (this.savedRange) {
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(this.savedRange)
      }
    },

    applyFontColor() {
      this.restoreSelection()
      this.$refs.editorContent.focus()
      document.execCommand('foreColor', false, this.fontColor) // eslint-disable-line no-restricted-properties
      this.syncContent()
    },

    applyHighlightColor() {
      this.restoreSelection()
      this.$refs.editorContent.focus()
      document.execCommand('hiliteColor', false, this.highlightColor) // eslint-disable-line no-restricted-properties
      this.syncContent()
    },

    confirmInsertLink() {
      if (this.linkUrl && this.linkUrl !== 'https://') {
        this.$refs.editorContent.focus()
        this.execCmd('createLink', this.linkUrl)
      }
    },

    confirmInsertTable() {
      const rows = this.tableRows || 3
      const cols = this.tableCols || 3
      let html = '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse; border-color:#ddd; width:100%; margin:8px 0;">'
      for (let r = 0; r < rows; r += 1) {
        html += '<tr>'
        for (let c = 0; c < cols; c += 1) {
          html += r === 0
            ? '<th style="background:#f0f0f0; text-align:left; padding:8px;">&nbsp;</th>'
            : '<td style="padding:8px;">&nbsp;</td>'
        }
        html += '</tr>'
      }
      html += '</table><p><br></p>'
      this.$refs.editorContent.focus()
      this.execCmd('insertHTML', html)
    },

    insertHorizontalRule() {
      this.execCmd('insertHTML', '<hr style="border:none; border-top:1px solid #ccc; margin:12px 0;">')
    },

    insertNamePlaceholder() {
      this.$refs.editorContent.focus()
      const span = '<span class="name-placeholder" style="background:#eeedfd; color:#7367f0; padding:1px 6px; border-radius:3px; font-family:monospace; font-size:12px;" contenteditable="false">name-placeholder</span>&nbsp;'
      this.execCmd('insertHTML', span)
    },

    toggleSignature() {
      const editor = this.$refs.editorContent
      if (this.signatureInserted) {
        const sig = editor.querySelector('[data-signature="true"]')
        if (sig) sig.remove()
        this.signatureInserted = false
      } else {
        editor.innerHTML += `<div data-signature="true">${this.signatureHtml}</div>`
        this.signatureInserted = true
      }
      this.syncContent()
    },

    toggleHtmlView() {
      if (this.showHtmlSource) {
        this.$refs.editorContent.innerHTML = this.form.content
      } else {
        this.form.content = this.$refs.editorContent.innerHTML
      }
      this.showHtmlSource = !this.showHtmlSource
    },

    // --- Submit ---
    handleSubmit() {
      this.nameTouched = true
      if (!this.form.name.trim()) {
        this.submitError = 'Name is required'
        return
      }

      this.submitting = true
      this.submitError = null

      const payload = new URLSearchParams()
      payload.append('name', this.form.name)
      payload.append('description', this.form.description || '')
      payload.append('content', this.form.content || '')

      const config = {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }

      const request = this.isEditMode
        ? axios.patch(`/emails/email_templates/${this.templateToEdit.id}/`, payload, config)
        : axios.post('/emails/email_templates/', payload, config)

      request
        .then(() => {
          this.submitting = false
          this.$emit('template-saved')
        })
        .catch(error => {
          this.submitting = false
          this.submitError = error?.response?.data?.error
            || error?.response?.data?.detail
            || 'Something went wrong while saving the template'
        })
    },
  },
}
</script>

<style scoped>
.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: #f8f8f8;
  border: 1px solid #ebe9f1;
  border-bottom: none;
  border-radius: 0.428rem 0.428rem 0 0;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 1px;
}

.toolbar-divider {
  display: inline-block;
  width: 1px;
  height: 22px;
  background: #ebe9f1;
  margin: 0 4px;
}

.toolbar-spacer {
  flex: 1;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 7px;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  color: #6e6b7b;
  transition: all 0.15s;
  font-size: 13px;
  font-weight: 600;
  font-family: Georgia, serif;
  line-height: 1;
}
.toolbar-btn:hover {
  background: #ebe9f1;
  color: #5e5873;
}
.toolbar-btn.active {
  background: #7367f0;
  color: #fff;
}

.toolbar-btn-text {
  font-family: inherit;
  font-weight: 500;
}
.toolbar-btn-text span {
  font-size: 11px;
  white-space: nowrap;
}

.toolbar-select {
  padding: 3px 6px;
  border: 1px solid #ebe9f1;
  border-radius: 0.25rem;
  font-size: 11px;
  color: #6e6b7b;
  background: #fff;
  cursor: pointer;
  outline: none;
  font-family: inherit;
}

.color-btn {
  position: relative;
  cursor: pointer;
  padding: 3px 6px;
  flex-direction: column;
  gap: 1px;
}
.color-letter {
  font-size: 14px;
  font-weight: 800;
  font-family: Georgia, serif;
  line-height: 1;
}
.color-bar {
  width: 16px;
  height: 3px;
  border-radius: 1px;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.editor-body {
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #ebe9f1;
  border-radius: 0 0 0.428rem 0.428rem;
  background: #fff;
}

.editor-content {
  min-height: 300px;
  padding: 14px 16px;
  outline: none;
  font-size: 14px;
  line-height: 1.7;
  color: #6e6b7b;
}
.editor-content:empty::before {
  content: 'Start typing your template...';
  color: #b9b9c3;
  pointer-events: none;
}

.editor-content:deep(table) {
  border-collapse: collapse;
  width: 100%;
}
.editor-content:deep(td),
.editor-content:deep(th) {
  border: 1px solid #ddd;
  padding: 8px;
}

.html-source {
  width: 100%;
  min-height: 300px;
  padding: 14px 16px;
  border: none;
  outline: none;
  resize: vertical;
  font-family: 'Fira Code', 'Cascadia Code', monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #6e6b7b;
  background: #f8f8f8;
  tab-size: 2;
  box-sizing: border-box;
}
</style>
